import React, { useCallback, useContext, useMemo, useState, createContext, Dispatch, SetStateAction } from 'react'
import { makeStyles } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Grid, { GridSize } from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import GameCategoryInput from '../../../../components/admin/GameCategoryInput'
import GameIdInput from '../../../../components/admin/GameIdInput'
import DropDown, { PropTypes as DropDownProps } from '../../../../components/default/form/DropDown'
import FormField from '../../../../components/default/form/FormField'
import CoreTable from '../../../../components/default/present/CoreTable'
import NumberInput from '../../../../components/default/form/NumberInput'
import FormSubmitButton from '../../../../components/default/form/FormSubmitButton'
import LoadingAndErrorFrame from '../../../../components/default/frames/LoadingAndErrorFrame'
import DateInputBase from '../../../../components/default/form/DateInput'
import MemoTextWithTitleAndContext from '../../../default/memo/MemoTextWithTitleAndContext'
import MemoPopoverWithContext from '../../../default/memo/MemoPopoverWithContext'
import { MemoPopoverPayload, createDefaultMemoPopoverPayload } from '../../../default/memo/MemoPopover'
import Tip from '../../../admin/Tip'
import useGDK from '../../../../providers/admin/gdk/useGDK'
import useT from '../../../../i18ns/admin/useT'
import { useCommonStyles } from '../../../../utils/admin/StyleHook'
import { PageFlowType } from '../../../../utils/default/PageFlowHook'
import { useGetData, useReload, useRedirectHandleBack } from '../../../../utils/default/ComplexFlowHook'
import { createTableData, formatMoney } from '../../../../utils/default/TableHelper'
import { getCashInputProps, getCodeInputProps } from '../../../../utils/default/FormHelper'
import { BcMath } from '@golden/bcmath'
import { Path } from '../../../../components/admin/route/route'
import { FormType, FormContext } from '../../../../views/admin/activity/ActivityWalletCreatePage'
import { ActivityWalletLimitType, GameCategoryType, ActivityWalletCsvRes, ActivityWalletUserRes } from '@golden/gdk-admin'
import { endOfDay, addDays } from 'date-fns'
import ActivityWalletSortActivityTitleDialog from './ActivityWalletSortActivityTitleDialog'

interface RowType {
  id: number
  account: string
  cash: string
  content: string | React.ReactElement
  bindingDeposits: React.ReactElement
  barrierSum: string
  check: string | React.ReactElement
}

const useStyles = makeStyles((theme) => ({
  divider: {
    backgroundColor: '#eee',
    margin: '32px 0'
  },
  error: {
    color: theme.palette.error.main
  },
  multiInput: {
    minHeight: '100px'
  }
}))

const multipleInputProps = {
  allowNegative: false,
  decimalScale: 0,
  maxLength: undefined
}

const cashInputProps = {
  ...getCashInputProps(),
  decimalScale: 4,
  maxLength: undefined
}

const codeInputProps = getCodeInputProps()

const MemoContext = createContext<[MemoPopoverPayload, Dispatch<SetStateAction<MemoPopoverPayload>>]>([
  createDefaultMemoPopoverPayload(),
  () => { }
])

const TextField = React.memo(MuiTextField)

const LimitTypeDropDown: React.FC<{ disabled?: boolean }> = React.memo(({ disabled = false }) => {
  const { value, handleChange, dispatch } = useContext(FormContext)
  const { t } = useT()
  const limitTypeOptions = useMemo(() => (
    [
      {
        name: t('common.gameCategoryType'),
        value: ActivityWalletLimitType.GAME_CATEGORY
      },
      {
        name: t('common.gamePlatform'),
        value: ActivityWalletLimitType.GAME
      }
    ]
  ), [t])

  const onChange = useCallback(handleChange('limit_type'), [])

  return (
    <DropDown
      value={value.limit_type}
      options={limitTypeOptions}
      fullWidth
      required
      label={t('common.activityGamePlatform')}
      disabled={disabled}
      onChange={(e) => {
        onChange(e)
        dispatch({ type: 'change', label: 'limit_ids', value: [] })
      }}
    />
  )
})

const LimitIdsDropDown: React.FC<{ disabled?: boolean }> = React.memo(({ disabled = false }) => {
  const { value, dispatch } = useContext(FormContext)

  return value.limit_type === ActivityWalletLimitType.GAME_CATEGORY
    ? (
      <GameCategoryInput
        value={value.limit_ids}
        fullWidth
        multiple
        noAll
        filterNoStopSaleGame
        required
        filterNoCategories={[GameCategoryType.LOTTERY]}
        onChange={(value) => {
          dispatch({ type: 'change', label: 'limit_ids', value })
        }}
        disabled={disabled}
      />
    )
    : (
      <GameIdInput
        value={value.limit_ids}
        fullWidth
        multiple
        noAll
        active
        required
        filterNoCategories={[GameCategoryType.LOTTERY]}
        disabled={disabled}
        onChange={(value) => {
          dispatch({ type: 'change', label: 'limit_ids', value })
        }}
      />
    )
})

const DateInput: React.FC<{ disabled?: boolean }> = React.memo(({ disabled = false }) => {
  const classes = useCommonStyles()
  const { t } = useT()
  const { value, handleChange } = useContext(FormContext)
  const grid = useMemo(() => ({
    xs: 6 as GridSize,
    md: 6 as GridSize,
    lg: 6 as GridSize
  }), [])
  const option = useMemo(() => ({
    label: t('common.receivedExpiredTime'),
    grid,
    disabled
  }), [t, grid])

  const setOneDay = () => {
    return { start: null, end: endOfDay(addDays(new Date(), 1)) }
  }
  const setSevenDay = () => {
    return { start: null, end: endOfDay(addDays(new Date(), 7)) }
  }

  const setThirtyDay = () => {
    return { start: null, end: endOfDay(addDays(new Date(), 30)) }
  }

  const tools = useMemo(() => {
    return [
      {
        label: t('common.countDay', { day: 1 }),
        change: setOneDay,
        disabled
      },
      {
        label: t('common.countDay', { day: 7 }),
        change: setSevenDay,
        disabled
      },
      {
        label: t('common.countDay', { day: 30 }),
        change: setThirtyDay,
        disabled
      }
    ]
  }, [t])

  const dateClasses = useMemo(() => ({
    button: classes.pinkGradualButton
  }), [classes.pinkGradualButton])

  const onChange = useCallback(handleChange('expired_at'), [])
  return (
    <DateInputBase
      value={value.expired_at}
      onChange={onChange}
      end={option}
      tools={tools}
      toolsGrid={grid}
      classes={dateClasses}
      required
      denseToolbar
    />
  )
})

const EffectiveCashInput: React.FC = React.memo(() => {
  const { t } = useT()
  const { value: form, handleChange } = useContext(FormContext)
  const value = useMemo(() => BcMath.base(Number(form.amount ?? 0)).mul(Number(form.multiple ?? 0)).get(), [form])

  return (
    <TextField
      value={value}
      label={t('common.addEffectiveCash')}
      disabled
      fullWidth
    />
  )
})

export const Step2Csv: React.FC<{
  resCsv: ActivityWalletCsvRes | null,
  pageFlow: PageFlowType
  options: Array<{ value: number, name: string }>
  setOptions: React.Dispatch<React.SetStateAction<Array<{
    value: number
    name: string
  }>>>
}> = React.memo(({ resCsv, pageFlow, options, setOptions }) => {
  const { t } = useT()
  const gdk = useGDK()
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const tableClasses = useMemo(() => ({
    head: commonClasses.greyTableHead,
    row: commonClasses.tableRow,
    cellHead: commonClasses.tableCellHead,
    cellBody: commonClasses.nowrap
  }), [commonClasses])

  const { value: form } = useContext(FormContext)
  const [open, setOpen] = useState(false)
  const { reloadFlag, reload } = useReload()

  const rows = useMemo(() => {
    return resCsv?.data.map((item, index): RowType => {
      return {
        id: index,
        account: item.account,
        cash: formatMoney(item.amount),
        content: (<MemoTextWithTitleAndContext context={MemoContext} title={item.content ?? '-'} memo={item.content ?? '-'} />),
        bindingDeposits: (<span className={commonClasses.pre}>{item.binding_deposits.join('\n')}</span>),
        barrierSum: form.multiple ? formatMoney(BcMath.base(item.amount).mul(form.multiple).get()) : '',
        check: (
          <>
            {item.is_account_duplicated && (<p className={classes.error}>{t('error.accountDuplicated')}</p>)}
            {item.is_warning_amount && (<p className={classes.error}>{t('error.cashTooHigh')}</p>)}
            {item.is_debit_card_unbound && (<p className={classes.error}>{t('error.notAllowedToParticipateActivity')}</p>)}
          </>
        )
      }
    }) ?? []
  }, [t, resCsv?.data, classes, form.multiple, commonClasses])
  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id',
          width: 240
        },
        account: {
          label: t('common.playerAccount'),
          value: 'account',
          align: 'center',
          width: 240
        },
        cash: {
          label: t('common.cash'),
          value: 'cash',
          align: 'center'
        },
        content: {
          label: t('common.activityContent'),
          value: 'content',
          align: 'center'
        },
        bindingDeposits: {
          label: t('common.exchangedDepositOrderNo'),
          value: 'bindingDeposits',
          align: 'center'
        },
        barrierSum: {
          label: t('common.addEffectiveCash'),
          value: 'barrierSum',
          align: 'center'
        },
        check: {
          label: t('common.check'),
          value: 'check',
          align: 'center'
        }
      },
      [
        'account',
        'cash',
        'content',
        'bindingDeposits',
        'barrierSum',
        'check'
      ],
      rows,
      'id'
    )
  }, [rows, t])

  useGetData({
    canLoadData: pageFlow.status.showContent,
    gdkFunc: () => gdk.activity.getActivityWalletActivityTitle(),
    gdkFuncDependencies: [gdk, reloadFlag],
    onSuccess: (data) => {
      setOptions(data.map(item => ({ name: item.title, value: item.id })))
    }
  })
  const [, handleDebouncedBack] = useRedirectHandleBack({ path: Path.ACTIVITY_WALLET })

  return form.file ? (
      <>
        <Grid item xs={12}>
            <MemoPopoverWithContext memoPopoverContext={MemoContext}>
              {
                (
                  <CoreTable
                    data={data}
                    total={0}
                    classes={tableClasses}
                  />
                )
              }
            </MemoPopoverWithContext>
        </Grid>
        <Grid item>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormField<FormType, DropDownProps>
                      context={FormContext}
                      component={DropDown}
                      name="activityWalletTitleId"
                      options={options}
                      label={t('common.activityName')}
                      placeholder={t('placeholder.pleaseChoose', { item: '' })}
                      fullWidth
                      required
                    />
                    <Box paddingTop={1} display="flex" justifyContent="flex-end">
                      <span className={commonClasses.anchor} onClick={() => { setOpen(true) }}>
                        {t('common.updateActivityNameList')}
                      </span>
                      <ActivityWalletSortActivityTitleDialog open={open} onClose={() => { setOpen(false) }} reloadOptions={reload} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<FormType, TextFieldProps>
                      context={FormContext}
                      component={NumberInput}
                      name="multiple"
                      label={`${t('common.deltaEffectiveCash')}${t('common.multiple')}`}
                      inputProps={multipleInputProps}
                      placeholder={''}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LimitTypeDropDown />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LimitIdsDropDown />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DateInput />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormField<FormType, TextFieldProps>
                      context={FormContext}
                      component={NumberInput}
                      name="code"
                      fullWidth
                      required
                      inputProps={codeInputProps}
                      label={t('common.googleCode')}
                      placeholder={t('placeholder.inputGoogleCode2')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container justifyContent="space-between">
              <Grid item>
                <Tip text={t('common.createActivityWalletTip')} />
              </Grid>
              <Grid item>
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      className={commonClasses.greyButton}
                      onClick={handleDebouncedBack}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <FormSubmitButton
                      component={Button}
                      context={FormContext}
                      className={commonClasses.purpleGradualButton}
                      type="submit"
                    >
                      {t('common.confirm')}
                    </FormSubmitButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
        </Grid>
      </>
  ) : null
})

export const Step2User: React.FC<{
  resUser: ActivityWalletUserRes | null,
  pageFlow: PageFlowType
  options: Array<{ value: number, name: string }>
  setOptions: React.Dispatch<React.SetStateAction<Array<{
    value: number
    name: string
  }>>>
}> = React.memo(({ resUser, pageFlow, options, setOptions }) => {
  const { t } = useT()
  const gdk = useGDK()
  const { value: form } = useContext(FormContext)
  const commonClasses = useCommonStyles()
  const classes = useStyles()

  const contentInputProps = useMemo(() => ({
    maxLength: 255 + (form.activityContent.match(/\n/g) || []).length
  }), [form.activityContent])

  const [open, setOpen] = useState(false)
  const { reloadFlag, reload } = useReload()
  const disabled = useMemo(() => resUser?.is_not_activity, [resUser])

  useGetData({
    canLoadData: pageFlow.status.showContent,
    gdkFunc: () => gdk.activity.getActivityWalletActivityTitle(),
    gdkFuncDependencies: [gdk, reloadFlag],
    onSuccess: (data) => {
      setOptions(data.map(item => ({ name: item.title, value: item.id })))
    }
  })
  const [, handleDebouncedBack] = useRedirectHandleBack({ path: Path.ACTIVITY_WALLET })

  return form.account ? (
      <Grid item>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={3}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  label={t('common.playerAccount')}
                  name="account"
                  error={resUser?.is_not_activity}
                  helperText={t(resUser?.is_not_activity ? 'error.isNotActivity' : '')}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={NumberInput}
                  label={t('common.cash')}
                  inputProps={cashInputProps}
                  disabled={disabled}
                  name="amount"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={NumberInput}
                label={`${t('common.effectiveCash')}${t('common.multiple')}`}
                  inputProps={multipleInputProps}
                  disabled={disabled}
                  name="multiple"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <EffectiveCashInput />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormField<FormType, DropDownProps>
                  context={FormContext}
                  component={DropDown}
                  name="activityWalletTitleId"
                  options={options}
                  label={t('common.activityName')}
                  placeholder={t('placeholder.pleaseChoose', { item: '' })}
                  disabled={disabled}
                  fullWidth
                  required
                />
                <Box paddingTop={1} display="flex" justifyContent="flex-end">
                  <span className={commonClasses.anchor} onClick={() => { setOpen(true) }}>
                    {t('common.updateActivityNameList')}
                  </span>
                  <ActivityWalletSortActivityTitleDialog open={open} onClose={() => { setOpen(false) }} reloadOptions={reload} />
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <LimitTypeDropDown disabled={disabled} />
              </Grid>
              <Grid item xs={12} md={3}>
                <LimitIdsDropDown disabled={disabled} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={6}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  label={t('common.activityContent')}
                  name="activityContent"
                  placeholder={t('placeholder.inputActivityWalletActivityContent')}
                  disabled={disabled}
                  fullWidth
                  type="text"
                  margin="normal"
                  variant="outlined"
                  multiline
                  inputProps={contentInputProps}
                  InputProps={{
                    classes: { input: classes.multiInput }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  label={t('common.bindingDeposits')}
                  name="bindingDeposits"
                  placeholder={t('placeholder.inputActivityWalletBindingDeposit')}
                  disabled={disabled}
                  fullWidth
                  type="text"
                  margin="normal"
                  variant="outlined"
                  multiline
                  InputProps={{
                    classes: { input: classes.multiInput }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={6}>
                <DateInput disabled={disabled} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField<FormType, TextFieldProps>
                  context={FormContext}
                  component={NumberInput}
                  name="code"
                  fullWidth
                  required
                  inputProps={codeInputProps}
                  label={t('common.googleCode')}
                  disabled={disabled}
                  placeholder={t('placeholder.inputGoogleCode2')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Grid container justifyContent="space-between">
          <Grid item>
            <Tip text={t('common.createActivityWalletTip')} />
          </Grid>
          <Grid item>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button
                  className={commonClasses.greyButton}
                  onClick={handleDebouncedBack}
                >
                  {t('common.cancel')}
                </Button>
              </Grid>
              <Grid item>
                <FormSubmitButton
                  component={Button}
                  context={FormContext}
                  className={commonClasses.purpleGradualButton}
                  disabled={disabled}
                  type="submit"
                >
                  {t('common.confirm')}
                </FormSubmitButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
  ) : null
})
